import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { loadStripe } from '@stripe/stripe-js';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Row, Col,
} from 'reactstrap';
import {
    purchaseWithSavedCard,
    confirmSavedCardAfterAction,
    billingActionReset,
} from 'redux/Billing/BillingActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';
import { getEffectivePrice, isTrialValid, getSpecialtyFields, ADDON_DISCOUNT_PERCENT } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const QuickBuyModal = ({ isOpen, toggle, plan, defaultCard, userDetails, onUseDifferentCard, activeSpecialty = 'pmr', isAddon = false, addonPrice = null, targetSpecialty = null, isBundle = false, bundle = null, bundlePlanIds = null, bundleTotalPrice = 0 }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!plan || !defaultCard) return null;
    if (isBundle && !bundle) return null;

    const purchaseSpecialty = targetSpecialty || activeSpecialty;
    const fields = getSpecialtyFields(purchaseSpecialty);
    const hasActiveTrial = isTrialValid(userDetails?.[fields.trialStartDate]);
    const price = isBundle ? bundleTotalPrice : (addonPrice != null ? addonPrice : getEffectivePrice(plan, hasActiveTrial));
    const brand = (defaultCard.brand || 'card').toUpperCase();

    const handleConfirm = async () => {
        setError('');
        setLoading(true);

        // Capture pre-purchase subscription state to know if this is an extension
        const hadActiveSubscription = !!(
            userDetails?.[fields.subscriptionEndDate] &&
            new Date(userDetails[fields.subscriptionEndDate]) > new Date()
        );

        try {
            const result = await dispatch(purchaseWithSavedCard({
                planId: plan.id || plan.priceId,
                paymentMethodId: defaultCard.id,
                specialty: purchaseSpecialty,
                isAddon,
                isBundle,
                bundleId: bundle?.id || '',
            }));

            let newEndDate = result?.subscriptionEndDate;

            if (result?.requiresAction && result.clientSecret) {
                // Handle 3DS client-side
                const stripe = await stripePromise;
                if (!stripe) {
                    throw new Error('Failed to load Stripe');
                }
                const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
                    clientSecret: result.clientSecret,
                    confirmParams: {
                        return_url: window.location.origin + '/client/billing',
                    },
                    redirect: 'if_required',
                });
                if (confirmError) {
                    throw new Error(confirmError.message || 'Card authentication failed');
                }
                if (paymentIntent?.status === 'succeeded') {
                    const confirmResult = await dispatch(confirmSavedCardAfterAction(paymentIntent.id, result.invoiceId, { specialty: purchaseSpecialty, isAddon, isBundle, bundleId: bundle?.id || '' }));
                    newEndDate = confirmResult?.subscriptionEndDate || newEndDate;
                } else {
                    throw new Error(`Payment status: ${paymentIntent?.status}`);
                }
            }

            // Refresh user data to reflect new subscription
            await dispatch(getUserData({ onLogin: true }));
            dispatch(billingActionReset());

            toggle();
            history.push({
                pathname: '/client/congrats',
                state: {
                    plan: isBundle ? bundle.name : plan.name,
                    isExtension: hadActiveSubscription,
                    subscriptionEndDate: newEndDate,
                    isAddon,
                    isBundle,
                    bundleSpecialties: isBundle ? bundle.specialties : null,
                    specialty: purchaseSpecialty,
                },
            });
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Payment failed';
            setError(msg);
            setLoading(false);
        }
    };

    const handleUseDifferentCard = () => {
        if (loading) return;
        if (onUseDifferentCard) onUseDifferentCard();
    };

    return (
        <Modal isOpen={isOpen} toggle={loading ? undefined : toggle} size="md" backdrop={loading ? 'static' : true}>
            <ModalHeader toggle={loading ? undefined : toggle}>Confirm Purchase</ModalHeader>
            <ModalBody>
                <div className="text-center mb-3">
                    <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mb-3">
                        <i className={isBundle ? "fas fa-layer-group" : "fas fa-credit-card"} />
                    </div>
                    <h4 className="mb-1">{isBundle ? bundle.name : plan.name}</h4>
                    <p className="text-muted mb-0">{plan.months} month{plan.months !== 1 ? 's' : ''} of full access</p>
                    {isBundle && bundle && (
                        <div className="mt-2">
                            <span className="badge badge-warning" style={{ fontSize: '0.9rem', padding: '6px 14px' }}>
                                <i className="fas fa-layer-group mr-1" />
                                Bundle — Save {bundle.discountPercent}%
                            </span>
                            <div className="mt-2">
                                {bundle.specialties.map(specId => (
                                    <span key={specId} className={`badge badge-${getSpecialty(specId).color} mr-1`} style={{ fontSize: '0.8rem', padding: '4px 10px' }}>
                                        <i className={`${getSpecialty(specId).icon} mr-1`} />
                                        {getSpecialty(specId).name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {isAddon && !isBundle && (
                        <span className="badge badge-danger mt-2" style={{ fontSize: '0.9rem', padding: '6px 14px' }}>
                            <i className="fas fa-tag mr-1" />
                            Add-On — Save {ADDON_DISCOUNT_PERCENT}%
                        </span>
                    )}
                </div>

                <Row className="mt-4">
                    <Col xs="6">
                        <small className="text-muted d-block">Amount</small>
                        <span className="h3 font-weight-bold text-success">${price}</span>
                    </Col>
                    <Col xs="6">
                        <small className="text-muted d-block">Card</small>
                        <span className="font-weight-bold">
                            {brand} •••• {defaultCard.last4}
                        </span>
                    </Col>
                </Row>

                <div className="mt-4 p-3 bg-secondary rounded">
                    <small className="text-muted">
                        <i className="fas fa-lock mr-1" />
                        Your saved card will be charged immediately. You can manage your cards anytime from your Billing page.
                    </small>
                </div>

                {error && (
                    <div className="text-danger mt-3 small">
                        <i className="fas fa-exclamation-circle mr-1" />
                        {error}
                    </div>
                )}
            </ModalBody>
            <ModalFooter className="d-flex flex-column" style={{ alignItems: 'stretch' }}>
                <Button color="primary" block onClick={handleConfirm} disabled={loading}>
                    {loading
                        ? <><i className="fas fa-spinner fa-spin mr-2" /> Processing...</>
                        : <>Charge ${price} to {brand} •••• {defaultCard.last4}</>}
                </Button>
                <Button color="link" block onClick={handleUseDifferentCard} disabled={loading}>
                    Use a different card
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default QuickBuyModal;
